/**
 * RoleEditor — role & permission editor modal (#1419).
 *
 * Ported verbatim from the former `pages/Users.tsx` (only the mount point and
 * import paths change): the permission-matrix grid, the single-draft override
 * model, the singular grant/revoke loop (#943 contract), the partial-failure
 * re-baseline, and the close-refresh guarantee are all byte-equivalent. Owned
 * by the UsersPage wrapper and opened by the resource's `mode: 'handoff'` row
 * action (which hands control back to the page's `setEditingUser` closure).
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import {
  Button,
  Checkbox,
  Badge,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalDescription,
} from '@resource-kit/ui/react';
import { useUserManagement } from '@hooks/useAuth';
import { http } from '@lib/api';
import { resolveApiError } from '@lib/errors';
import { queryKeys } from '@/lib/queryKeys';
import { fetchGrantedPermissions } from '@/services/users';
import {
  PERMISSION_GROUPS,
  ACTIONS,
  ROLES,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  ROLE_PRESETS,
} from '@/permissions';

export function RoleEditor({
  userId,
  userName,
  userRole,
  currentUserId,
  open,
  onClose,
}: {
  userId: string | null;
  userName: string | null;
  userRole: string;
  currentUserId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const { updateUser } = useUserManagement();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const isSelf = userId !== null && userId === currentUserId;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The granted-permission set is server state — read it through a query
  // rather than copying it into `useState`. The query data IS the
  // baseline (`original*`); the operator's in-flight edits live in a
  // single "draft" slot that overrides the baseline until saved. Enabled
  // only while the editor is open for a real user; the parent remounts
  // this component per (userId, open) so drafts reset without a manual
  // reset effect.
  const permissionsQuery = useQuery({
    queryKey: queryKeys.users.permissions(userId ?? ''),
    queryFn: () => fetchGrantedPermissions(userId as string),
    enabled: open && userId !== null,
  });
  const loading = permissionsQuery.isLoading;
  // On fetch error we fall back to the role preset (and surface a
  // non-fatal warning below) so the grid still renders something usable.
  const originalPermissions =
    permissionsQuery.data ?? (permissionsQuery.isError ? ROLE_PRESETS[userRole] || [] : []);
  const originalRole = userRole;

  const [draftRole, setDraftRole] = useState<string | null>(null);
  const [draftPermissions, setDraftPermissions] = useState<string[] | null>(null);
  const role = draftRole ?? userRole;
  const permissions = draftPermissions ?? originalPermissions;

  // Detect if permissions differ from role preset
  const rolePreset = ROLE_PRESETS[role] || [];
  const isCustomized =
    permissions.length !== rolePreset.length ||
    permissions.some((p) => !rolePreset.includes(p)) ||
    rolePreset.some((p) => !permissions.includes(p));

  const roleChanged = role !== originalRole;
  const permsChanged =
    permissions.length !== originalPermissions.length ||
    permissions.some((p) => !originalPermissions.includes(p)) ||
    originalPermissions.some((p) => !permissions.includes(p));
  const hasChanges = roleChanged || permsChanged;

  const selfDemotion = isSelf && roleChanged && role !== 'admin';

  const togglePermission = (perm: string) => {
    setDraftPermissions((prev) => {
      const base = prev ?? originalPermissions;
      return base.includes(perm) ? base.filter((p) => p !== perm) : [...base, perm];
    });
  };

  const resetToRoleDefaults = () => {
    setDraftPermissions(ROLE_PRESETS[role] || []);
  };

  const handleRoleChange = (newRole: string) => {
    setDraftRole(newRole);
    setDraftPermissions(ROLE_PRESETS[newRole] || []);
  };

  const handleSave = async () => {
    if (!userId || !hasChanges || selfDemotion) return;
    setSaving(true);
    setError('');
    try {
      // 1. Update role if changed (this triggers provisionForRole on backend)
      if (roleChanged) {
        const res = await updateUser(userId, { role });
        if (!res.success) {
          setError(res.error || t('users.roleUpdateFailed'));
          return; // finally resets setSaving
        }
      }

      // 2. After role change, fetch fresh permissions to diff against
      let currentPerms = originalPermissions;
      if (roleChanged) {
        try {
          currentPerms = await fetchGrantedPermissions(userId);
        } catch {
          // Fallback: use role preset as baseline for diff
          currentPerms = ROLE_PRESETS[role] || [];
        }
      }

      // 3. Compute and apply permission diff
      const toGrant = permissions.filter((p) => !currentPerms.includes(p));
      const toRevoke = currentPerms.filter((p) => !permissions.includes(p));

      // The identity surface grants/revokes one permission per call
      // (#943 contract: singular `{ permission }` body), so apply the
      // diff sequentially. Grant = POST, revoke = DELETE with a body.
      for (const permission of toGrant) {
        await http.post(`/api/users/${userId}/permissions`, { permission });
      }
      for (const permission of toRevoke) {
        await http.delete(`/api/users/${userId}/permissions`, { data: { permission } });
      }

      // Role may have changed (shown in the list) and the permission set
      // moved — invalidate the whole users namespace so the list and any
      // open editor re-read authoritative state. `users.all()` is a
      // prefix of both the list and per-user permission keys.
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      onClose();
    } catch (err) {
      // The grant/revoke loop is not atomic: a mid-loop failure leaves the
      // backend partially changed while the draft still holds the operator's
      // intended set. Invalidate the whole users namespace: `users.all()` is
      // a prefix of BOTH the per-user permission key (forces a fresh
      // authoritative GET to re-baseline the diff) and the list key (the role
      // may already have been persisted before the loop threw, so the table
      // must re-sync to it). Drop only the permission draft so a retry diffs
      // against real backend state; keep the role draft since the role edit
      // is already applied and hasChanges must stay true for the operator to
      // retry the remaining permission edits. Best-effort — the original
      // error still surfaces below.
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      setDraftPermissions(null);
      setError(
        resolveApiError(err, i18n, {
          namespace: 'users',
          fallbackKey: 'users.permissionsUpdateFailed',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  // Restore main's close-refresh guarantee: the role may have changed
  // during this session (a successful role update followed by a partial
  // permission-loop failure leaves the list showing a persisted role the
  // editor never got to reconcile). Invalidate the users list on every
  // close so the table re-syncs to authoritative state, mirroring the old
  // refreshUsers()-on-close behaviour.
  const handleClose = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    onClose();
  };

  // A save-path error takes precedence; otherwise surface the non-fatal
  // permission-fetch warning (grid falls back to role defaults).
  const displayError =
    error ||
    (permissionsQuery.isError
      ? t('perm.fetchFailed', { defaultValue: 'Failed to load permissions, showing role defaults' })
      : '');

  return (
    <Modal isOpen={open} size="lg">
      <ModalHeader onClose={saving ? undefined : handleClose}>
        <ModalTitle>{t('users.changeRole')}</ModalTitle>
        <ModalDescription>{userName}</ModalDescription>
      </ModalHeader>
      <ModalBody>
        {displayError && (
          <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2 text-sm text-destructive">
            {displayError}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">{t('users.permRoleLabel')}</label>
          <select
            value={role}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(ROLE_LABELS[r], { defaultValue: ROLE_LABELS[r] })}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(ROLE_DESCRIPTIONS[role], { defaultValue: ROLE_DESCRIPTIONS[role] })}
          </p>
          {selfDemotion && (
            <p className="mt-1 text-xs text-destructive">
              {t('users.error.self_role_change_forbidden')}
            </p>
          )}
        </div>

        {/* Customization indicator + reset button */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{t('users.permResourceCol')}</span>
            {isCustomized && (
              <Badge variant="secondary" className="text-xs">
                {t('perm.customized')}
              </Badge>
            )}
          </div>
          {isCustomized && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToRoleDefaults}
              className="text-xs h-7 gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              {t('perm.resetToRole')}
            </Button>
          )}
        </div>

        <div className="border border-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">{t('ui.loading')}</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary">
                  <th className="px-4 py-2 text-left font-medium">{t('users.permResourceCol')}</th>
                  {ACTIONS.map((a) => (
                    <th key={a} className="px-4 py-2 text-center font-medium capitalize w-20">
                      {a}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {PERMISSION_GROUPS.map((g) => (
                  <tr key={g.resource}>
                    <td className="px-4 py-2.5 font-medium">
                      {t(g.label, { defaultValue: g.label })}
                    </td>
                    {ACTIONS.map((a) => {
                      const perm = `${g.resource}:${a}`;
                      const validAction = (g.actions as readonly string[]).includes(a);
                      if (!validAction) {
                        return (
                          <td key={a} className="px-4 py-2.5 text-center text-muted-foreground/20">
                            —
                          </td>
                        );
                      }
                      const has = permissions.includes(perm);
                      return (
                        <td key={a} className="px-4 py-2.5 text-center">
                          <Checkbox
                            checked={has}
                            onCheckedChange={() => togglePermission(perm)}
                            disabled={role === 'admin'}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {role === 'admin' && (
          <p className="mt-2 text-xs text-muted-foreground">{t('users.adminFullAccess')}</p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={handleClose} disabled={saving}>
          {t('ui.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving || !hasChanges || selfDemotion}>
          {saving ? t('profile.saving') : t('ui.save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
